import React, { useCallback, useState } from 'react';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import CheckCircle from '../../../../assets/svgComponents/CheckCircle';
import Button from '../../../../components/shared/Button/Button';
import Modal from '../../../../components/shared/Modal/Modal';
import { CURRENCIES } from '../../../../constants/currenciesConstants';
import { createDispute } from '../../../../redux/actions/disputeActions';

import styles from './DisputeModal.module.scss';

const DisputeModal = ({ rowData, show, setShow }) => {
  const { t } = useTranslation('common');
  const [showNext, setShowNext] = useState(false);
  const [showFinal, setShowFinal] = useState(false);
  const [reason, setReason] = useState(0);
  const [description, setDescription] = useState('');

  const dispatch = useDispatch();

  const DISPUTE_REASONS = [
    t('dispute.paymentDetailsIncorrect'),
    t('dispute.paymentStatusIncorrect'),
    t('dispute.other'),
  ];

  const handleHideFirstPage = useCallback(() => {
    setReason(0);
    setDescription('');
    setShow(false);
  }, []);
  const handleHideSecondPage = useCallback(() => {
    setReason(0);
    setDescription('');
    setShowNext(false);
  }, []);

  const handleNext = useCallback(() => {
    setShow(false);
    setShowNext(true);
  }, []);

  const handleSuccessModal = useCallback(() => {
    setReason(0);
    setDescription('');
    setShowNext(false);
    setShowFinal(true);
  }, []);

  const handleSubmit = useCallback(() => {
    dispatch(createDispute(reason, description, rowData.id, handleSuccessModal));
  }, [reason, description, rowData.id, handleSuccessModal]);

  const getReward = (value) => {
    switch (value) {
      case 'MonetaryReward':
        return t('dispute.monetaryReward');
      case 'ExperientialReward':
        return t('dispute.experientialReward');
      default:
        return t('dispute.reward');
    }
  };

  return (
    <>
      <Modal size="lg" show={show} onHide={handleHideFirstPage}>
        <Modal.CustomHeader closeButton>
          <Modal.Title>{t('dispute.title')}</Modal.Title>
        </Modal.CustomHeader>
        <Form>
          <Modal.Body className={styles.formModal}>
            <div className={styles.body}>
              <div className={`${styles.bodyTitle} pb-2`}>{t('dispute.selectReason')}</div>
              <div className="mb-3">
                <Form.Check
                  label={t('dispute.paymentDetailsIncorrect')}
                  value="0"
                  type="radio"
                  id="radio-1"
                  checked={reason === 0}
                  onChange={(e) => setReason(+e.target.value)}
                />
                <Form.Check
                  label={t('dispute.paymentStatusIncorrect')}
                  value="1"
                  type="radio"
                  id="radio-2"
                  checked={reason === 1}
                  onChange={(e) => setReason(+e.target.value)}
                />
                <Form.Check
                  label={t('dispute.other')}
                  value="2"
                  type="radio"
                  id="radio-3"
                  checked={reason === 2}
                  onChange={(e) => setReason(+e.target.value)}
                />
                <Form.Label>
                  {t('dispute.additionalDetails')}
                  {reason === 2 ? '*' : ` ${t('dispute.optional')}`}
                </Form.Label>
                <InputGroup>
                  <Form.Control
                    onChange={(e) => setDescription(e.target.value)}
                    as="textarea"
                    aria-label="With textarea"
                    placeholder={t('dispute.additionalComments')}
                    className={styles.additionalComments}
                  />
                </InputGroup>
                <div className={styles.bodyNote}>{t('dispute.processingNote')}</div>
              </div>
            </div>
          </Modal.Body>
          <Modal.CustomFooter className="justify-content-center">
            <Button variant="outline-primary" onClick={handleHideFirstPage} className={styles.modalBtn}>
              {t('cancel')}
            </Button>
            <Button
              variant="primary"
              disabled={reason === 2 && description === ''}
              onClick={handleNext}
              className={styles.modalBtn}
            >
              {t('next')}
            </Button>
          </Modal.CustomFooter>
        </Form>
      </Modal>

      <Modal size="lg" show={showNext} onHide={handleHideSecondPage}>
        <Modal.CustomHeader closeButton>
          <Modal.Title>{t('dispute.title')}</Modal.Title>
        </Modal.CustomHeader>
        <Modal.Body className={styles.detailsModal}>
          <div className={styles.body}>
            <div className={styles.userContent}>
              <div className={`${styles.content} row`}>
                <div className="col">
                  <div className={styles.label}>{t('dispute.employerName')}</div>
                  <div className={styles.description}>{rowData.employer}</div>
                </div>
                <div className="col">
                  <div className={styles.label}>{t('dispute.openingTitle')}</div>
                  <div className={styles.description}>{rowData.opening}</div>
                </div>
              </div>
              <div className={`${styles.content} row`}>
                <div className={styles.label}>{t('dispute.candidateName')}</div>
                <div className={styles.description}>{rowData.candidate}</div>
              </div>
            </div>
            <div className={styles.disputeContent}>
              <div className={styles.content}>
                <div className={styles.label}>{t('dispute.paymentStatus')}</div>
                <div className={styles.description}>{DISPUTE_REASONS[reason]}</div>
              </div>
              {description && (
                <div className={styles.content}>
                  <div className={styles.label}>{t('description')}</div>
                  <div className={styles.description}>{description}</div>
                </div>
              )}
              <div className={styles.content}>
                <div className={styles.label}>{t('dispute.rewardDetails')}</div>
                <div className={styles.description}>{getReward(rowData.rewardable_type)}</div>
                <div className={styles.description}>
                  {rowData.rewardable_type === 'MonetaryReward' && CURRENCIES[rowData.reward_currency || 'USD']}
                  {rowData.reward}
                </div>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.CustomFooter className="justify-content-center">
          <Button variant="outline-primary" onClick={handleHideSecondPage} className={styles.modalBtn}>
            {t('cancel')}
          </Button>
          <Button variant="primary" onClick={handleSubmit} className={styles.modalBtn}>
            {t('submit')}
          </Button>
        </Modal.CustomFooter>
      </Modal>

      <Modal size="lg" show={showFinal} onHide={() => setShowFinal(false)}>
        <Modal.CustomHeader closeButton>
          <Modal.Title>{t('dispute.successTitle')}</Modal.Title>
        </Modal.CustomHeader>
        <Form>
          <Modal.Body className={styles.successModal}>
            <div className={styles.body}>
              <div className={styles.icon}>
                <CheckCircle />
              </div>
              <div>
                {t('dispute.successMessage')}
                <br />
                {t('dispute.successFollowUp')}
              </div>
            </div>
          </Modal.Body>
          <Modal.CustomFooter className="justify-content-center">
            <Button variant="primary" onClick={() => setShowFinal(false)} className={styles.modalBtn}>
              {t('dispute.ok')}
            </Button>
          </Modal.CustomFooter>
        </Form>
      </Modal>
    </>
  );
};

export default DisputeModal;
